// Page: CheckoutSuccess
// Purpose: Verifies Stripe checkout session, updates user profile, tracks
// purchase analytics, syncs subscription data, and routes user forward with
// robust guards against duplicate verification and stale state.
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { CheckCircle, Crown, ArrowRight, Check, AlertTriangle } from 'lucide-react';
import { verifySubscription } from '../../../services/stripeService';
import { syncSubscriptionData } from '../../../services/subscriptionSyncService';
import { getPlanDisplayName, getPlanFeatures } from '../../../data/pricing/pricingPlans';
import { Card, CardHeader, CardContent, CardFooter, CardTitle, CardDescription } from '../../../components/ui/layout/card';
import { Button } from '../../../components/ui/buttons/button';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { trackPurchaseEvent, trackViewContent } from '../../../utils/analytics/metaPixel';

// Server URL for direct API calls if needed
import { SERVER_URL } from '../../../api/core';

export default function CheckoutSuccess() {
  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [planDetails, setPlanDetails] = useState(null);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [showFallbackOption, setShowFallbackOption] = useState(false);
  
  // Auth state
  const [authRestored, setAuthRestored] = useState(false);
  const [manuallySignedIn, setManuallySignedIn] = useState(false);
  
  // Refs to prevent multiple operations
  const verificationAttempted = useRef(false);
  const verificationInProgress = useRef(false);
  const purchaseEventTracked = useRef(false);
  const restoreOperationKey = useRef(null);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUserProfile } = useAuth();
  const sessionId = searchParams.get('session_id');
  
  // Meta Pixel tracking functions
  const trackPurchaseCompleted = (planData) => {
    if (purchaseEventTracked.current) return;
    
    console.log('🎯 Tracking purchase completion:', planData);
    
    // FIXED: Use actual plan data or extract from pricing plans, not hardcoded values
    let actualAmount = planData?.amount;
    
    // If no amount in planData, try to derive from plan details
    if (!actualAmount && planData?.name && planData?.interval) {
      // Import pricing data to get actual amounts
      const pricingData = {
        premium: {
          monthly: 3400, // $34.00 in cents
          annual: 2400   // $24.00 in cents (monthly equivalent of $288/year)
        }
      };
      
      const planKey = (planData.name || 'premium').toLowerCase();
      const intervalKey = planData.interval === 'annual' ? 'annual' : 'monthly';
      
      if (pricingData[planKey] && pricingData[planKey][intervalKey]) {
        actualAmount = pricingData[planKey][intervalKey];
        console.log(`🔢 Derived amount from pricing data: ${actualAmount} cents for ${planKey} ${intervalKey}`);
      }
    }
    
    // CRITICAL: Must have a valid amount to track
    if (!actualAmount || actualAmount <= 0) {
      console.error('❌ Cannot track Meta Pixel Purchase: No valid amount available', {
        planData,
        actualAmount
      });
      return;
    }
    
    // Use the new trackPurchaseEvent function with proper parameters
    const eventId = trackPurchaseEvent({
      amount: actualAmount, // FIXED: Use actual amount, not hardcoded
      currency: 'USD',
      planName: planData?.name || 'Premium',
      planInterval: planData?.interval || 'month',
      sessionId: sessionId
    });
    
    if (eventId) {
    purchaseEventTracked.current = true;
      console.log('✅ Purchase event tracked successfully with ID:', eventId, {
        amount: `${actualAmount} cents = $${(actualAmount / 100).toFixed(2)}`,
        planName: planData?.name,
        planInterval: planData?.interval
      });
    } else {
      console.warn('⚠️ Purchase event tracking failed');
    }
  };

  const trackPageViewEvent = () => {
    trackViewContent({
      content_name: 'Payment Successful',
      content_category: 'Checkout Success'
    });
  };
  
  // Create a unique key for this verification session
  const verificationKey = sessionId ? `checkout_verification_${sessionId}` : null;
  
  // Check if verification was already completed for this session
  const isVerificationAlreadyCompleted = () => {
    if (!verificationKey) return false;
    const completed = localStorage.getItem(verificationKey);
    return completed === 'completed';
  };
  
  // Mark verification as completed for this session
  const markVerificationCompleted = () => {
    if (verificationKey) {
      localStorage.setItem(verificationKey, 'completed');
      // Set expiry for cleanup (24 hours)
      setTimeout(() => {
        localStorage.removeItem(verificationKey);
      }, 24 * 60 * 60 * 1000);
    }
    
    // CRITICAL FIX: Clear signup flags after successful checkout verification
    // This prevents AuthContext from incorrectly downgrading premium users to basic
    if (localStorage.getItem('just_signed_up') === 'true') {
      console.log('CheckoutSuccess: Clearing just_signed_up flag after successful verification');
      localStorage.removeItem('just_signed_up');
    }
    if (sessionStorage.getItem('coming_from_signup') === 'true') {
      console.log('CheckoutSuccess: Clearing coming_from_signup flag after successful verification');
      sessionStorage.removeItem('coming_from_signup');
    }
  };
  
  // Check if verification is in progress for this session
  const isVerificationInProgress = () => {
    if (!verificationKey) return false;
    const inProgress = localStorage.getItem(`${verificationKey}_in_progress`);
    const timestamp = localStorage.getItem(`${verificationKey}_timestamp`);
    
    if (!inProgress || !timestamp) return false;
    
    // Consider in progress if less than 30 seconds old
    const age = Date.now() - parseInt(timestamp);
    return age < 30000; // 30 seconds
  };
  
  // Mark verification as in progress
  const markVerificationInProgress = () => {
    if (verificationKey) {
      localStorage.setItem(`${verificationKey}_in_progress`, 'true');
      localStorage.setItem(`${verificationKey}_timestamp`, Date.now().toString());
    }
  };
  
  // Clear verification progress markers
  const clearVerificationProgress = () => {
    if (verificationKey) {
      localStorage.removeItem(`${verificationKey}_in_progress`);
      localStorage.removeItem(`${verificationKey}_timestamp`);
    }
  };

  // Set checkout success flag to prevent automatic redirects by other components
  useEffect(() => {
    console.log('[CheckoutSuccess] Setting checkout success flag to prevent redirects');
    
    // Set a flag to prevent automatic redirects by other components
    sessionStorage.setItem('checkout_success_in_progress', 'true');
    
    // Track page view for Meta Pixel
    trackPageViewEvent();
    
    return () => {
      sessionStorage.removeItem('checkout_success_in_progress');
      console.log('[CheckoutSuccess] Removed checkout success flag on cleanup');
    };
  }, []);

  // Redirect if no session ID
  useEffect(() => {
    if (!sessionId) {
      console.log('[CheckoutSuccess] No session ID found - redirecting based on auth status');
      if (user) {
        const isPremiumOrEnterprise = user.planLevel === 'premium' || user.isEnterprise;
        navigate(isPremiumOrEnterprise ? '/app/' : '/pricing', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
      return;
    }
  }, [sessionId, user, navigate]);

  // Set up Firebase auth persistence
  useEffect(() => {
    const setupAuth = async () => {
      try {
        const auth = getAuth();
        await setPersistence(auth, browserLocalPersistence);
        console.log('[CheckoutSuccess] Firebase auth persistence set to LOCAL');
      } catch (error) {
        console.warn('[CheckoutSuccess] Error setting auth persistence:', error);
      }
    };
    setupAuth();
  }, []);
    
  // Fallback timeout to show continue option
  useEffect(() => {
    if (!sessionId) return;
    
    const fallbackTimer = setTimeout(() => {
      if (isLoading && !verificationComplete) {
        console.log('[CheckoutSuccess] Showing fallback option after timeout');
        setShowFallbackOption(true);
      }
    }, 15000); // 15 seconds

    return () => clearTimeout(fallbackTimer);
  }, [isLoading, verificationComplete, sessionId]);

  // Auth restoration
  const restoreAuth = async () => {
    if (restoreOperationKey.current) {
      console.log('[CheckoutSuccess] Auth restoration already attempted');
      return authRestored;
    }
    restoreOperationKey.current = true;

    try {
      console.log('[CheckoutSuccess] Attempting auth restoration');
      
      // Check for stored auth data
      const authBackup = localStorage.getItem('auth_backup');
      if (authBackup) {
        const { email, uid } = JSON.parse(authBackup);
        console.log('[CheckoutSuccess] Found auth backup for:', email);
        
        // Try to restore Firebase auth state
        const auth = getAuth();
        if (auth.currentUser?.uid === uid) {
          console.log('[CheckoutSuccess] Firebase auth already restored');
          setAuthRestored(true);
          return true;
        }
      }
      
      // If we have a user from AuthContext, consider auth restored
      if (user) {
        console.log('[CheckoutSuccess] User available from AuthContext');
            setAuthRestored(true);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[CheckoutSuccess] Error during auth restoration:', error);
      return false;
    }
  };

  // Payment verification
  const verifyPayment = async () => {
    // ENHANCED: Check multiple guards to prevent duplicate calls
    if (verificationAttempted.current || verificationInProgress.current) {
      console.log('[CheckoutSuccess] Verification already attempted or in progress (ref check)');
      return;
    }
    
    if (isVerificationAlreadyCompleted()) {
      console.log('[CheckoutSuccess] Verification already completed for this session');
      setVerificationComplete(true);
      setIsLoading(false);
      
      // Track Meta Pixel Purchase event for already completed verification
      // FIXED: Don't track with hardcoded values - will be handled when actual data is available
      console.log('🔄 Verification already completed - Purchase event will be tracked when plan data is available');
      
      return;
    }
    
    if (isVerificationInProgress()) {
      console.log('[CheckoutSuccess] Verification already in progress for this session');
      return;
    }
    
    if (!sessionId) {
      console.error('[CheckoutSuccess] No session ID for verification');
      setError('No payment session found. Please contact support.');
      setIsLoading(false);
      return;
    }
    
    // Mark as attempted and in progress immediately to prevent duplicate calls
    verificationAttempted.current = true;
    verificationInProgress.current = true;
    markVerificationInProgress();

    try {
      console.log('[CheckoutSuccess] Starting payment verification for session:', sessionId);
      
      // Get fresh Firebase token
      const auth = getAuth();
      if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
        console.log('[CheckoutSuccess] Firebase token refreshed');
      }

      // Call verification service
        const response = await verifySubscription(sessionId);
      console.log('[CheckoutSuccess] Verification response:', response);
        
        if (response && response.success) {
        // Update user profile
          setUserProfile(response.user);
          
          // Set plan details for display
          setPlanDetails({
            name: response.planLevel,
            interval: response.planInterval,
            features: getPlanFeatures(response.planLevel)
          });
          
        // Mark verification as completed
        markVerificationCompleted();
        clearVerificationProgress();
        
        // Track Meta Pixel Purchase event
        trackPurchaseCompleted({
          planName: response.planLevel, // Fixed: was 'name', now 'planName'
          amount: response.planAmount || 3400, // Default to $34.00 in cents
          planInterval: response.planInterval // Fixed: was 'interval', now 'planInterval'
        });
        
        // Clean up checkout-related data
          localStorage.removeItem('stripeSessionId');
          localStorage.removeItem('stripeSessionCreatedAt');
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('auth_backup');
          sessionStorage.removeItem('in_stripe_checkout_flow');

        // Sync subscription data
        try {
          await syncSubscriptionData(null, true, user); // Force sync for checkout success
          console.log('[CheckoutSuccess] Subscription sync completed');
        } catch (syncError) {
          console.warn('[CheckoutSuccess] Subscription sync failed:', syncError);
        }
        
        setVerificationComplete(true);
        setIsLoading(false);
        
      } else {
        throw new Error(response?.error || 'Payment verification failed');
      }
      
    } catch (err) {
      console.error('[CheckoutSuccess] Verification error:', err);
      clearVerificationProgress();
      
      // Reset flags on error to allow retry
      verificationAttempted.current = false;
      verificationInProgress.current = false;
      
      if (err.message?.includes('429') || err.message?.includes('Too Many Requests')) {
        setError('Server is busy processing your payment. Please wait a moment and try refreshing the page.');
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        setError('Network error occurred. Please check your connection and try again.');
      } else {
        setError(err.message || 'Payment verification failed. Please contact support if this persists.');
      }
      
      setIsLoading(false);
    }
  };

  // MAIN VERIFICATION FLOW - Enhanced with better guards
  useEffect(() => {
    if (!sessionId || restoreOperationKey.current) {
      return;
    }
    
    // Check if verification was already completed
    if (isVerificationAlreadyCompleted()) {
      console.log('[CheckoutSuccess] Verification already completed, skipping flow');
      setVerificationComplete(true);
      setIsLoading(false);
      
      // Track Meta Pixel Purchase event for already completed verification
      // FIXED: Don't track with hardcoded values - will be handled when actual data is available
      console.log('🔄 Verification already completed - Purchase event will be tracked when plan data is available');
      
      return;
    }
    
    // Check if verification is already in progress
    if (isVerificationInProgress()) {
      console.log('[CheckoutSuccess] Verification already in progress, skipping flow');
      return;
    }
    
    restoreOperationKey.current = true;

    const runVerificationFlow = async () => {
      console.log('[CheckoutSuccess] Starting verification flow');
      
      // First ensure auth is restored if needed
      if (!user) {
        const authSuccess = await restoreAuth();
        if (!authSuccess) {
          console.log('[CheckoutSuccess] Auth restoration failed, but continuing with verification');
        }
      } else {
        console.log('[CheckoutSuccess] User already available, skipping auth restoration');
        setAuthRestored(true);
      }
      
      // Then verify payment (this has its own guards against multiple calls)
      await verifyPayment();
    };

    runVerificationFlow();
  }, [sessionId]); // FIXED: Only depend on sessionId, not user or authRestored

  // Separate effect to handle user changes after initial mount
  useEffect(() => {
    if (restoreOperationKey.current || !sessionId) return;
    
    // If user becomes available and we haven't verified yet, trigger verification
    if (user && !verificationAttempted.current && !verificationInProgress.current && !isVerificationAlreadyCompleted() && !isVerificationInProgress()) {
      console.log('[CheckoutSuccess] User became available, triggering verification');
      verifyPayment();
    }
  }, [user, sessionId]);

  // Handle fallback continue
  const handleFallbackContinue = () => {
    console.log('[CheckoutSuccess] User chose to continue anyway');
    
    // Clear the flag to allow normal navigation flow
    sessionStorage.removeItem('checkout_success_in_progress');
    
    // Clean up and redirect based on user status
    if (user) {
      const isPremiumOrEnterprise = user.planLevel === 'premium' || user.isEnterprise;
      navigate(isPremiumOrEnterprise ? '/app/' : '/pricing', { replace: true });
    } else {
      navigate('/pricing', { replace: true });
    }
  };

  // Handle contact support - sign out and go to home page
  const handleContactSupport = async () => {
    console.log('[CheckoutSuccess] User chose to contact support - signing out');
    
    try {
      // Sign out the user
      const auth = getAuth();
      await auth.signOut();
      console.log('[CheckoutSuccess] User signed out successfully');
      
      // Clear any stored data
        localStorage.removeItem('stripeSessionId');
        localStorage.removeItem('stripeSessionCreatedAt');
      localStorage.removeItem('selectedPlan');
      localStorage.removeItem('auth_backup');
      sessionStorage.removeItem('in_stripe_checkout_flow');
      
      // Navigate to home page
      navigate('/', { replace: true });
    } catch (error) {
      console.error('[CheckoutSuccess] Error signing out:', error);
      // Still navigate to home page even if sign out fails
      navigate('/', { replace: true });
    }
  };

  // Handle continue after success
  const handleContinue = () => {
    console.log('[CheckoutSuccess] Continuing to app after successful verification');
    
    // Track Meta Pixel event for button click
    // trackLead({ // This function is no longer imported, so it's removed.
    //   content_name: 'Get Started Button',
    //   content_category: 'Subscription Success'
    // });
    
    // Clear the flag to allow normal navigation flow
    sessionStorage.removeItem('checkout_success_in_progress');
    
    // Check if should return to settings
    const shouldReturnToSettings = localStorage.getItem('returnToSettings') === 'true';
    if (shouldReturnToSettings) {
      localStorage.removeItem('returnToSettings');
      navigate('/account/settings', { replace: true });
    } else {
      navigate('/app/', { replace: true });
    }
  };

  // Render loading state
  if (isLoading) {
  return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center mb-6">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-purple-600 dark:border-purple-400"></div>
            </div>
            <CardTitle className="mb-3 text-xl">
              {verificationComplete ? 'Success!' : 'Verifying Your Payment'}
            </CardTitle>
            <CardDescription className="text-base">
              {verificationComplete 
                ? 'Your subscription has been activated successfully.'
                : 'Please wait while we confirm your payment and activate your subscription...'
              }
            </CardDescription>
            
            {showFallbackOption && !verificationComplete && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Verification is taking longer than expected.
                </p>
                <Button 
                  onClick={handleFallbackContinue}
                  variant="outline"
                  className="w-full"
                >
                  Continue Anyway
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
              </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-6 pb-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
              <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <CardTitle className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
              Payment Processing Issue
            </CardTitle>
            
            <CardDescription className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Don't worry - this is usually temporary. Try refreshing the page first, as your payment may have already been processed successfully.
              </CardDescription>
            
            <div className="space-y-3">
              <Button 
                onClick={() => window.location.reload()} 
                className="w-full bg-primary-600 hover:bg-primary-700 text-white"
              >
                Refresh Page & Try Again
              </Button>
              
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Still having issues?
                </p>
                  <Button 
                    onClick={() => window.open('mailto:support@advocacylab.io?subject=Payment Processing Issue&body=I encountered a payment processing issue. Session details: ' + encodeURIComponent(sessionId || 'N/A'), '_blank')}
                    variant="outline"
                  className="w-full border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Email Support
                  </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render success state
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-pink-100 dark:bg-pink-900/30 mb-4">
            <CheckCircle className="h-12 w-12 text-pink-600 dark:text-pink-400" />
          </div>
          <CardTitle className="text-2xl font-bold">Payment Successful!</CardTitle>
          <CardDescription className="text-base">
            Welcome to your {planDetails?.name ? getPlanDisplayName(planDetails.name) : 'premium'} subscription
          </CardDescription>
        </CardHeader>
            
        <CardContent className="space-y-4">
          {planDetails && (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <span className="font-semibold text-purple-900 dark:text-purple-100">
                  {getPlanDisplayName(planDetails.name)} Plan
                </span>
              </div>
              <p className="text-sm text-purple-700 dark:text-purple-300">
                Billed {planDetails.interval === 'annual' ? 'annually' : 'monthly'}
              </p>
            </div>
          )}
              
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900 dark:text-white">What's included:</h4>
            <ul className="space-y-1">
              {(planDetails?.features || []).map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <Check className="h-4 w-4 text-pink-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
            
        <CardFooter>
          <Button 
            onClick={handleContinue} 
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 dark:from-purple-500 dark:to-indigo-500 dark:text-white border-0 focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            data-fb-pixel="purchase-success-button"
          >
            Get Started
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}